import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle, ArrowRight, Layers, MapPin, Users, Thermometer, Rocket } from "lucide-react";

const HowItWorks: React.FC = () => {
  return (
    <div className="container mx-auto px-4 py-12 max-w-4xl">
      <Helmet>
        <title>How It Works | SaunaDip Directory</title>
        <meta name="description" content="Discover how the SaunaDip Directory helps guests find authentic sauna and plunge experiences, and helps operators reach more people and manage their listings." />
      </Helmet>
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-sauna-navy flex items-center justify-center gap-2">
          <MapPin className="h-8 w-8 text-sauna-orange" />
          How It Works
          <Layers className="h-8 w-8 text-sauna-blue" />
        </h1>
        <div className="h-1 w-24 bg-gradient-to-r from-sauna-orange to-sauna-blue rounded-full mx-auto mt-4"></div>
      </div>

      {/* Directory Section - Main Focus */}
      <Card className="mb-10 border-none shadow-lg">
        <CardContent className="p-8">
          <div className="flex items-center mb-6">
            <MapPin className="h-7 w-7 text-sauna-orange mr-3" />
            <h2 className="text-2xl font-bold text-sauna-navy">The SaunaDip Directory: Discover & Share Authentic Experiences</h2>
          </div>
          <p className="text-lg text-sauna-navy/90 mb-6">
            The SaunaDip Directory is your go-to place to find and share authentic sauna and plunge experiences. Guests can browse a curated list of locations, complete with live data, honest details, and real reviews. Operators can list their location to reach more guests, build credibility, and manage their presence with ease.
          </p>
          <div className="grid md:grid-cols-2 gap-8 text-left mb-8">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-sauna-orange mt-1" />
              <div>
                <span className="font-medium text-sauna-navy">For guests:</span>
                <p className="text-sauna-navy/70 text-base">Browse high-integrity sauna and plunge experiences, see live data (where available), and trust what you find—no hidden details.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-sauna-orange mt-1" />
              <div>
                <span className="font-medium text-sauna-navy">For operators:</span>
                <p className="text-sauna-navy/70 text-base">Get listed to boost your visibility, credibility, and bookings. Manage your listing anytime, and join a trusted wellness community.</p>
              </div>
            </div>
          </div>
          <div className="text-center">
            <Link to="/directory" className="inline-flex items-center gap-2 px-6 py-3 bg-sauna-blue text-white rounded-lg font-semibold text-lg shadow hover:bg-sauna-orange transition-colors duration-200">
              Explore Directory <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* Operator Benefits Section - Directory Focused */}
      <Card className="mb-10 border-none shadow-lg">
        <CardContent className="p-8">
          <div className="flex items-center mb-6">
            <Users className="h-7 w-7 text-sauna-orange mr-3" />
            <h2 className="text-2xl font-bold text-sauna-navy">Why Operators Love the Directory</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8 text-left mt-4">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-sauna-orange mt-1" />
              <div>
                <span className="font-medium text-sauna-navy">Reach new customers</span>
                <p className="text-sauna-navy/70 text-base">Get discovered by wellness seekers looking for unique sauna and plunge experiences.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-sauna-orange mt-1" />
              <div>
                <span className="font-medium text-sauna-navy">Showcase your location</span>
                <p className="text-sauna-navy/70 text-base">Add photos, details, and (if available) live data to build trust and attract bookings.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-sauna-orange mt-1" />
              <div>
                <span className="font-medium text-sauna-navy">Easy management</span>
                <p className="text-sauna-navy/70 text-base">Claim and update your listing anytime, no technical skills required.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-sauna-orange mt-1" />
              <div>
                <span className="font-medium text-sauna-navy">Join a trusted community</span>
                <p className="text-sauna-navy/70 text-base">Be part of a growing network of high-integrity operators and wellness pioneers.</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Call to Action Section */}
      <Card className="mb-10 border-none shadow-lg">
        <CardContent className="p-8 text-center">
          <div className="flex items-center justify-center mb-6">
            <Rocket className="h-7 w-7 text-sauna-orange mr-3" />
            <h2 className="text-2xl font-bold text-sauna-navy">Ready to Get Listed?</h2>
          </div>
          <p className="text-lg text-sauna-navy/80 mb-6">Put your sauna or plunge experience on the map with the SaunaDip Directory. No commitment required to sign up!</p>
          <Link to="/signup" className="inline-flex items-center gap-2 px-8 py-3 bg-sauna-blue text-white rounded-lg font-semibold text-lg shadow hover:bg-sauna-orange transition-colors duration-200">
            Start for Free <ArrowRight className="w-5 h-5" />
          </Link>
        </CardContent>
      </Card>
    </div>
  );
};

export default HowItWorks; 